'use client'

import {createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {AuthPayloadResponse, UserSummary, WorkspaceSummary} from "@/schemas/auth.response";
import {AuthApiError, loginRequest, logoutRequest, meRequest, registerRequest} from "@/lib/auth";
import {AuthOverlayLoader} from "@/components/ui/AuthOverlayLoader";
import {usePathname} from "next/navigation";

interface AuthContextValue {
    user: UserSummary | null;
    workspaces: WorkspaceSummary[];
    currentWorkspace: WorkspaceSummary | null;
    isAuthenticated: boolean;
    authError: string | null;
    isAuthLoading: boolean;
    login: (email: string, password: string, remember: boolean) => Promise<AuthPayloadResponse>;
    register: (email: string, password: string, displayName: string, firstName?: string, lastName?: string) => Promise<AuthPayloadResponse>;
    logout: () => Promise<void>;
    loadMe: () => Promise<AuthPayloadResponse | null>;
    applyAuthPayload: (payload: AuthPayloadResponse) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({children}: { children: ReactNode }) {
    const [user, setUser] = useState<UserSummary | null>(null);
    const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([]);
    const [currentWorkspace, setCurrentWorkspace] = useState<WorkspaceSummary | null>(null);
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [authError, setAuthError] = useState<string | null>(null);
    const [pendingAuthRequests, setPendingAuthRequests] = useState<number>(0);
    const [loadingText, setLoadingText] = useState<string>("Lade Benutzerdaten...");
    const pathname = usePathname()

    const withAuthLoading = useCallback(async <T, >(text: string, action: () => Promise<T>): Promise<T> => {
        setLoadingText(text);
        setPendingAuthRequests((value) => value + 1);
        try {
            return await action();
        } finally {
            setPendingAuthRequests((value) => Math.max(0, value - 1));
        }
    }, []);

    const applyAuthPayload = useCallback((payload: AuthPayloadResponse) => {
        setAuthError(null);
        setUser(payload.user ?? null);
        setWorkspaces(payload.workspaces ?? []);
        setCurrentWorkspace(payload.currentWorkspace ?? null);
        setIsAuthenticated(Boolean(payload.user));
    }, []);

    const login = useCallback(async (email: string, password: string, remember: boolean) => {
        const payload = await withAuthLoading("Anmeldung läuft...", () => loginRequest({email, password, remember}));
        applyAuthPayload(payload);
        return payload;
    }, [applyAuthPayload, withAuthLoading]);

    const register = useCallback(async (email: string, password: string, displayName: string, firstName?: string, lastName?: string) => {
        const payload = await withAuthLoading(
            "Registrierung läuft...",
            () => registerRequest({email, password, displayName, firstName, lastName})
        );
        applyAuthPayload(payload);
        return payload;
    }, [applyAuthPayload, withAuthLoading]);

    const logout = useCallback(async () => {
        await withAuthLoading("Abmeldung läuft...", () => logoutRequest());
        setUser(null);
        setWorkspaces([]);
        setCurrentWorkspace(null);
        setIsAuthenticated(false);
    }, [withAuthLoading]);

    const loadMe = useCallback(async () => {
        setAuthError(null);
        try {
            const payload = await withAuthLoading("Lade Benutzerdaten...", () => meRequest());
            applyAuthPayload(payload);
            return payload;
        } catch (e: unknown) {
            setAuthError(e instanceof AuthApiError && e.statusCode === 401
                ? "Deine Sitzung ist abgelaufen. Bitte erneut anmelden."
                : "Die Anmeldung konnte nicht geladen werden. Prüfe die Verbindung und versuche es erneut.");
            setUser(null);
            setWorkspaces([]);
            setCurrentWorkspace(null);
            setIsAuthenticated(false);

            if (e instanceof AuthApiError) {
                if (e.statusCode === 409 || e.payload?.status === "WORKSPACE_SELECTION_REQUIRED") {
                    if (!pathname.includes("select-workspace") && !pathname.includes("create-workspace")) {
                        window.location.assign("/select-workspace");
                    }
                }
            }
            return null;
        }
    }, [applyAuthPayload, pathname, withAuthLoading]);

    useEffect(() => {
        if (!isAuthenticated && !user) {
            loadMe();
        }
    }, [isAuthenticated, user, loadMe]);

    const value = useMemo<AuthContextValue>(() => ({
        user,
        workspaces,
        currentWorkspace,
        isAuthenticated,
        authError,
        isAuthLoading: pendingAuthRequests > 0,
        login,
        register,
        logout,
        loadMe,
        applyAuthPayload,
    }), [user, workspaces, currentWorkspace, isAuthenticated, authError, pendingAuthRequests, login, register, logout, loadMe, applyAuthPayload]);

    return (
        <AuthContext.Provider value={value}>
            <AuthOverlayLoader open={pendingAuthRequests > 0} text={loadingText} />
            {children}
        </AuthContext.Provider>
    );
}

export function useAuthContext() {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error("useAuthContext must be used inside AuthProvider");
    }
    return ctx;
}
